import { useMemo, Fragment, useState } from 'react';
import { GYPSUM, LORAX, CALYPSO, KOALA } from 'HubStyleTokens/colors';
import styled, { css } from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Big from 'UIComponents/elements/Big';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITable from 'UIComponents/table/UITable';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIImage from 'UIComponents/image/UIImage';
import * as FieldTypes from '../constants/dataSync/FieldType';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DataObject = styled(Big).withConfig({
  displayName: "SharedDataCard__DataObject",
  componentId: "sc-8428m2-0"
})(["text-transform:none;font-weight:500;"]);
const rowHeightStyles = css(["height:48px;td{height:48px !important;}"]);
const DataSyncDropDownTR = styled.tr.withConfig({
  displayName: "SharedDataCard__DataSyncDropDownTR",
  componentId: "sc-8428m2-1"
})(["cursor:", ";", ""], props => props.opsHub ? 'pointer' : 'initial', rowHeightStyles);
const FieldMappingsCaret = ({
  open
}) => /*#__PURE__*/_jsx(UIIcon, {
  className: "p-right-4",
  name: open ? 'down' : 'right',
  size: "xxs",
  color: CALYPSO
});
/*
  For Ops Hub listings only
 */
function SharedDataCard({
  dataSync,
  iconUrl,
  name,
  onOpenToolTip,
  onToggleOpen,
  opsHub,
  SyncIcon
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const {
    sync,
    target,
    data
  } = dataSync;
  const renderedFieldMappings = useMemo(() => {
    return Object.values(FieldTypes).map(fieldType => {
      const mappings = dataSync.getFieldMappingsByType(fieldType);
      if (!mappings.length) {
        return null;
      }
      return /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx("tr", {
          "data-test-id": "field-mapping-dropdown",
          children: /*#__PURE__*/_jsx("td", {
            colSpan: 3,
            style: {
              fontWeight: 500
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `marketplace-ui-apps-core.dataSyncs.fieldMappings.${fieldType}`
            })
          })
        }), mappings.map((mapping, index) => {
          const isCustomMapping = mapping.fieldType === FieldTypes.CUSTOM;
          return /*#__PURE__*/_jsxs("tr", {
            "data-test-id": "field-mapping",
            children: [/*#__PURE__*/_jsx("td", {
              colSpan: isCustomMapping ? 3 : 1,
              children: mapping.appField
            }), !isCustomMapping && /*#__PURE__*/_jsxs(Fragment, {
              children: [/*#__PURE__*/_jsx("td", {}), /*#__PURE__*/_jsx("td", {
                children: /*#__PURE__*/_jsx("span", {
                  className: "m-left-4",
                  children: mapping.hubSpotField
                })
              })]
            })]
          }, `default-mapping-${index}`);
        })]
      }, fieldType);
    });
  }, [dataSync]);
  return /*#__PURE__*/_jsx(UICardWrapper, {
    "data-test-id": "shared-data-card",
    style: {
      border: `1px solid ${KOALA}`
    },
    className: "m-bottom-6",
    children: /*#__PURE__*/_jsxs(UITable, {
      condensed: true,
      children: [/*#__PURE__*/_jsx("thead", {
        children: /*#__PURE__*/_jsxs(DataSyncDropDownTR, {
          "data-test-id": "data-sync-dropdown",
          opsHub: opsHub,
          onClick: () => {
            if (opsHub) {
              setDropdownOpen(!dropdownOpen);
              if (onToggleOpen) {
                onToggleOpen();
              }
            }
          },
          style: {
            height: 60,
            backgroundColor: GYPSUM
          },
          children: [/*#__PURE__*/_jsx("th", {
            style: {
              width: '40%'
            },
            children: /*#__PURE__*/_jsxs(UIFlex, {
              align: "center",
              children: [opsHub && /*#__PURE__*/_jsx(FieldMappingsCaret, {
                open: dropdownOpen
              }), /*#__PURE__*/_jsxs(UIFlex, {
                align: "center",
                children: [iconUrl && /*#__PURE__*/_jsx(UIImage, {
                  src: iconUrl,
                  width: 24,
                  className: "m-right-2"
                }), /*#__PURE__*/_jsx(DataObject, {
                  children: target
                })]
              })]
            })
          }), /*#__PURE__*/_jsx(UITooltip, {
            onOpenChange: ({
              target: {
                value: open
              }
            }) => {
              if (open && onOpenToolTip) {
                onOpenToolTip();
              }
            },
            placement: "top",
            title: opsHub ? /*#__PURE__*/_jsx(FormattedMessage, {
              message: `marketplace-ui-apps-core.dataSyncs.sync.${sync}`
            }) : dataSync.description || /*#__PURE__*/_jsx(FormattedMessage, {
              message: `marketplace-ui-apps-core.detail.overview.sharedData.dataFlow${dataSync.sync}`,
              options: {
                name
              }
            }),
            children: /*#__PURE__*/_jsx("th", {
              className: "p-top-3",
              style: {
                width: '10%'
              },
              children: /*#__PURE__*/_jsx(SyncIcon, {
                sync: sync
              })
            })
          }), /*#__PURE__*/_jsx("th", {
            style: {
              width: '50%'
            },
            children: /*#__PURE__*/_jsxs(UIFlex, {
              align: "center",
              children: [/*#__PURE__*/_jsx(UIIcon, {
                name: "sprocket",
                color: LORAX,
                size: "xs",
                className: "m-top-1 m-right-2 m-left-4"
              }), /*#__PURE__*/_jsx(DataObject, {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: `marketplace-ui-apps-core.dataSyncs.data.${data}`
                })
              })]
            })
          })]
        })
      }), /*#__PURE__*/_jsx("tbody", {
        children: dropdownOpen && renderedFieldMappings
      })]
    })
  }, target);
}
export default SharedDataCard;